import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PageHeader } from '@ant-design/pro-components';
import { LeftOutlined } from '@ant-design/icons';
import { PAGE_TITLE_SLOT_ID } from '../layouts/HeaderLayout/headerConstants';

const StyledHeaderTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  min-width: 0;

  h1 {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.2;
    color: var(--textColor);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  span {
    font-size: 13px;
    color: var(--dimGray);
    white-space: nowrap;
  }

  @media (max-width: 1100px) {
    h1 {
      display: -webkit-box;
      font-size: 22px;
      white-space: normal;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    span {
      display: none;
    }
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 18px;
    }

    span {
      display: none;
    }
  }
`;

/**
 * Custom page header component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.title - The title of the page.
 * @param {string} props.subTitle - Secondary text displayed next to the title.
 * @param {ReactNode} props.extra - Additional content to be displayed on the right side of the header.
 * @param {string} props.customBreadcrumbs - The type of custom breadcrumbs to be displayed.
 * @param {boolean} props.withoutBreadCrumb - Determines whether to display the breadcrumb or not.
 * @param {string} props.customPathName - The custom pathname to be used for generating breadcrumbs.
 * @param {Object} props.supervision - The supervision object used for generating custom breadcrumbs.
 * @param {ReactNode} props.backIcon - The custom back icon to be displayed.
 * @param {Function} props.onBack - The function to be called when the back button is clicked.
 * @returns {JSX.Element} The rendered page header component.
 */
export const PageHeaderCustom = ({
  title,
  subTitle,
  extra,
  customBreadcrumbs,
  withoutBreadCrumb,
  customPathName,
  supervision,
  backIcon,
  onBack,
  withoutBackIcon
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const [titleSlot, setTitleSlot] = useState(null);

  useEffect(() => {
    setTitleSlot(document.getElementById(PAGE_TITLE_SLOT_ID));
  }, []);

  const splitPathname = customPathName
    ? customPathName.split('/').filter((p) => p !== '')
    : pathname.split('/').filter((p) => p !== '');
  let tmpPath;
  const breadCrumbs = splitPathname
    .slice(0, splitPathname.length - 1)
    .map((p) => {
      tmpPath = `/${p}`;
      return {
        path: tmpPath,
        breadcrumbName:
          p === 'show' &&
          customBreadcrumbs === 'supervision' &&
          supervision?.reference
            ? t(`breadcrumbs.supervision`, supervision)
            : // Segments sans traduction (identifiants, types…) : non affichés
              t(`breadcrumbs.${p}`, { defaultValue: '' })
      };
    })
    .filter(({ breadcrumbName }) => breadcrumbName);
  const dynamicRoutes = [...breadCrumbs];

  const itemRender = (
    // eslint-disable-next-line react/prop-types
    { path, breadcrumbName, state, search, ...route },
    params,
    routesList
  ) => {
    const last = routesList.indexOf(route) === routesList.length - 1;
    return last ? (
      <span key={path}>{breadcrumbName}</span>
    ) : (
      <Link key={path} to={{ pathname: path, state, search }}>
        {breadcrumbName}
      </Link>
    );
  };

  // When the layout provides a slot, the title is displayed in the app header.
  const titleInHeader = titleSlot && title;
  const hasBody =
    extra || !withoutBreadCrumb || !withoutBackIcon || !titleInHeader;

  return (
    <>
      {titleInHeader &&
        createPortal(
          <StyledHeaderTitle>
            <h1>{title}</h1>
            {subTitle && <span>{subTitle}</span>}
          </StyledHeaderTitle>,
          titleSlot
        )}
      {hasBody && (
        <PageHeader
          breadcrumb={
            withoutBreadCrumb ? null : { items: dynamicRoutes, itemRender }
          }
          title={titleInHeader ? null : title}
          subTitle={titleInHeader ? null : subTitle}
          onBack={() => (onBack ? navigate(onBack) : navigate(-1))}
          ghost={false}
          extra={extra}
          backIcon={withoutBackIcon ? false : backIcon || <LeftOutlined />}
        />
      )}
    </>
  );
};

PageHeaderCustom.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  subTitle: PropTypes.string,
  extra: PropTypes.element,
  customBreadcrumbs: PropTypes.string,
  supervision: PropTypes.shape({
    reference: PropTypes.string
  }),
  withoutBreadCrumb: PropTypes.bool,
  customPathName: PropTypes.string,
  backIcon: PropTypes.bool,
  onBack: PropTypes.string,
  withoutBackIcon: PropTypes.bool
};

PageHeaderCustom.defaultProps = {
  extra: null,
  customBreadcrumbs: null,
  supervision: null,
  title: null,
  subTitle: null,
  withoutBreadCrumb: false,
  customPathName: null,
  backIcon: false,
  onBack: null,
  withoutBackIcon: false
};
